import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Checkbox, Button } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { CustomAutocomplete } from '../CustomAutocomplete/CustomAutocomplete';
import {
  useLabelAutocompleteInput,
  SELECT_ALL_VALUE,
} from './useLabelAutocompleteInput';
import { LabelAutocompleteInputProps } from './types';
import { LabelChips } from '../../DataFeed/Configuration/components/ConfigurationSections/InstrumentIdentifiers/components/RuleBuilder/components/LabelChips/LabelChips';
import './LabelAutocompleteInput.scss';

export const LabelAutocompleteInput: React.FC<
  LabelAutocompleteInputProps
> = props => {
  const {
    selectedColumn,
    labels,
    onRemove,
    placeholder,
    disabled,
    showCheckboxes = false,
  } = props;
  const [inputValue, setInputValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const labelsDisplayRef = useRef<HTMLDivElement>(null);
  const {
    handleChange,
    isAllSelected,
    handleSelectAll,
    customFilterOptions,
    options,
  } = useLabelAutocompleteInput({
    ...props,
    inputValue,
    onInputClear: () => setInputValue(''),
  });

  // Custom clear handler - only clear input text, don't remove selected labels
  const handleClearClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setInputValue('');
  };

  // Measure if content exceeds 100px and show expand button if needed
  useEffect(() => {
    // Measure after DOM updates to ensure accurate scrollHeight
    const frameId = requestAnimationFrame(() => {
      if (labelsDisplayRef.current && labels.length > 0) {
        const { scrollHeight } = labelsDisplayRef.current;
        setShowExpandButton(scrollHeight > 100);
      } else {
        setShowExpandButton(false);
      }
    });

    // Cleanup: cancel animation frame on unmount
    return () => cancelAnimationFrame(frameId);
  }, [labels]);

  return (
    <Box className="labelAutocompleteContainer">
      <CustomAutocomplete
        multiple
        freeSolo
        disableCloseOnSelect
        filterSelectedOptions={!showCheckboxes}
        filterOptions={customFilterOptions}
        options={options}
        value={labels?.map(l => l.value) ?? []}
        stopKeyPropagation={false}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          // Only update input value when user is actively typing
          // Ignore automatic clearing after selection (reason='reset')
          // Clear button is handled by custom handleClearClick handler
          if (reason === 'input') {
            setInputValue(newInputValue);
          }
        }}
        disabled={disabled || !selectedColumn}
        getOptionLabel={(option: string) =>
          option === SELECT_ALL_VALUE ? 'Select All' : option
        }
        placeholder={placeholder || 'Select or type label values...'}
        renderTags={() => null}
        slotProps={{
          clearIndicator: {
            onClick: handleClearClick,
          },
        }}
        renderOption={(props, option, { selected }) => {
          const isSelectAllOption = option === SELECT_ALL_VALUE;

          if (isSelectAllOption) {
            const { key, ...otherProps } = props;
            return (
              <li
                key={key}
                {...otherProps}
                className="autocompleteOption selectAllOption"
                onClick={e => {
                  e.stopPropagation();
                  handleSelectAll();
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  size="small"
                  sx={{ marginRight: 1 }}
                />
                <strong>Select All</strong>
              </li>
            );
          }

          const { key, ...otherProps } = props;
          return (
            <li key={key} {...otherProps} className="autocompleteOption">
              {showCheckboxes && (
                <Checkbox
                  checked={selected}
                  size="small"
                  sx={{ marginRight: 1 }}
                />
              )}
              {option}
            </li>
          );
        }}
      />
      <Box
        ref={labelsDisplayRef}
        className={`labelsDisplay ${isExpanded ? 'expanded' : 'collapsed'}`}
      >
        {labels.length === 0 ? (
          <Typography variant="caption" className="noLabelsText">
            No labels added yet
          </Typography>
        ) : (
          <LabelChips labels={labels} onRemove={onRemove} />
        )}
      </Box>
      {showExpandButton && (
        <Button
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
          className="expandButton"
        >
          {isExpanded ? 'Show Less' : 'Show More'}
        </Button>
      )}
    </Box>
  );
};
